import type { ReactNode } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { getAdsForSlot } from '../../lib/ads'
import type { AdItem, AdSlotLayout } from '../../types'
import { cn } from '../../lib/utils'

interface AdSlotProps {
  /** 对应 ads.json 里 slots 的 key */
  slotId: string
  className?: string
  /** 覆盖 JSON 里的 layout */
  layout?: AdSlotLayout
  /** 覆盖标题；传空字符串隐藏标题 */
  title?: string
  kind?: AdItem['kind'] | AdItem['kind'][]
}

export function AdSlot({ slotId, className, layout, title, kind }: AdSlotProps) {
  const result = getAdsForSlot(slotId, { kind })
  if (!result) return null

  const { slot, items } = result
  const resolvedLayout = layout ?? slot.layout
  const resolvedTitle = title !== undefined ? title : slot.title

  return (
    <section className={cn('w-full', className)} aria-label={resolvedTitle || '推广'}>
      {resolvedTitle ? (
        <div className="flex items-center gap-2 mb-3">
          <span className="h-px w-5 bg-primary/50" />
          <h2 className="text-[13px] font-semibold text-muted-foreground tracking-tight">
            {resolvedTitle}
          </h2>
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/60 font-medium">推广</span>
        </div>
      ) : null}

      {resolvedLayout === 'banner' && <BannerRow items={items} />}
      {resolvedLayout === 'cards' && <CardsGrid items={items} />}
      {resolvedLayout === 'stack' && <StackList items={items} />}
    </section>
  )
}

function AdLink({
  ad,
  className,
  children,
}: {
  ad: AdItem
  className?: string
  children: ReactNode
}) {
  return (
    <a
      href={ad.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={className}
    >
      {children}
    </a>
  )
}

function Badge({ text }: { text?: string }) {
  if (!text) return null
  return (
    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold">
      {text}
    </span>
  )
}

function BannerRow({ items }: { items: AdItem[] }) {
  return (
    <div className="grid lg:grid-cols-2 gap-3">
      {items.map((ad) => (
        <AdLink
          key={ad.id}
          ad={ad}
          className="group relative isolate min-h-[172px] overflow-hidden rounded-2xl bg-secondary/70 p-5 ring-1 ring-border/40 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-card via-card/90 to-primary/[0.07]" />
          <div className="relative flex h-full flex-col">
            <div className="flex items-start justify-between gap-4">
              <Badge text={ad.badge} />
              {ad.image ? (
                <img
                  src={ad.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 rounded-[22%] object-cover bg-white shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                />
              ) : null}
            </div>
            <div className="mt-auto flex items-end justify-between gap-4 pt-6">
              <div className="min-w-0">
                <h3 className="font-semibold text-[19px] leading-tight tracking-tight">{ad.title}</h3>
                <p className="mt-1 text-[13px] text-muted-foreground line-clamp-1">{ad.description}</p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-primary">
                {ad.cta || '了解更多'}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </div>
        </AdLink>
      ))}
    </div>
  )
}

function CardsGrid({ items }: { items: AdItem[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((ad) => (
        <AdLink
          key={ad.id}
          ad={ad}
          className="group flex flex-col rounded-2xl border border-border/60 bg-card p-4 hover:border-border hover:bg-secondary/40 transition-colors"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            {ad.image ? (
              <img
                src={ad.image}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-12 h-12 rounded-[22%] object-cover bg-white border border-black/5 dark:border-white/10 dark:bg-white/5"
              />
            ) : (
              <div className="w-12 h-12 rounded-[22%] bg-secondary" />
            )}
            <Badge text={ad.badge} />
          </div>
          <h3 className="font-semibold text-[14px] leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {ad.title}
          </h3>
          <p className="text-[12px] text-muted-foreground line-clamp-2 mt-1 flex-1">
            {ad.description}
          </p>
          <span className="mt-3 self-start h-7 px-3 rounded-full bg-secondary text-primary text-[12px] font-semibold inline-flex items-center">
            {ad.cta || '了解更多'}
          </span>
        </AdLink>
      ))}
    </div>
  )
}

function StackList({ items }: { items: AdItem[] }) {
  return (
    <div className="divide-y divide-border/50 border border-border/50 rounded-2xl overflow-hidden bg-card">
      {items.map((ad) => (
        <AdLink
          key={ad.id}
          ad={ad}
          className="group flex items-center gap-3 p-3.5 hover:bg-secondary/50 transition-colors"
        >
          {ad.image ? (
            <img
              src={ad.image}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-11 h-11 rounded-[22%] object-cover bg-white border border-black/5 dark:border-white/10 dark:bg-white/5 shrink-0"
            />
          ) : (
            <div className="w-11 h-11 rounded-[22%] bg-secondary shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
              <h3 className="font-semibold text-[14px] truncate group-hover:text-primary transition-colors">
                {ad.title}
              </h3>
              <Badge text={ad.badge} />
            </div>
            <p className="text-[12px] text-muted-foreground line-clamp-1">{ad.description}</p>
          </div>
          <span className="shrink-0 text-primary text-[12px] font-semibold">
            {ad.cta || '查看'}
          </span>
        </AdLink>
      ))}
    </div>
  )
}
